import * as React from "react";
import { useMemo, useState } from "react";
import Flexbox from "../../layout/Flexbox/Flexbox";
import SVGCross from "../../svg_icons/SVGCross";

import "../../../styles/main_style.css";
import "./Modal_style.css";

export type ModalProps = Readonly<{
    isVisible?: boolean;
    useDefaultStyle?: boolean;
    width?: number | null;
    height?: number | null;
    showCloseButton?: boolean;
    header?: string;
    footer?: string | React.ReactNode;
    onClose?: () => void;
    onConfirm?: (response?: any) => void;
    contentClassName?: string;
}>;

type Props = Readonly<ModalProps & { children: React.ReactNode }>;

export default function Modal({
    isVisible = false,
    useDefaultStyle = true,
    width = null,
    height = null,
    showCloseButton = true,
    header,
    footer,
    onClose,
    onConfirm,
    contentClassName,
    children,
}: Props): JSX.Element {
    const modalStyle = useMemo<React.CSSProperties>(() => {
        return { width: width ?? "auto", height: height ?? "auto" };
    }, [width, height]);

    const modalFooter: React.ReactNode = (() => {
        if (footer === null || footer === undefined) {
            return null;
        } else if (typeof footer === "string") {
            switch (footer) {
                case "standard-ok":
                    return <ModalOKFooter onConfirm={onConfirm} />;
                case "standard-ok-cancel":
                    return (
                        <ModalOKCancelFooter
                            onClose={onClose}
                            onConfirm={onConfirm}
                        />
                    );
                case "standard-submit-cancel":
                    return (
                        <ModalSubmitCancelFooter
                            onClose={onClose}
                            onConfirm={onConfirm}
                        />
                    );
                default:
                    return <div>{footer}</div>;
            }
        } else {
            return footer;
        }
    })();

    return (
        <>
            {isVisible === true && (
                <div
                    className={"modal-top-container".concat(
                        useDefaultStyle ? " default-style" : ""
                    )}
                    style={modalStyle}
                >
                    <Flexbox className="modal-header" direction="row">
                        {header != null && <h1>{header}</h1>}
                        {showCloseButton && (
                            <div
                                className="flex-row flex-end"
                                style={{ flex: 0 }}
                            >
                                <div onClick={onClose}>
                                    <SVGCross className="x-button" />
                                </div>
                            </div>
                        )}
                    </Flexbox>
                    <div
                        className={`modal-content-container${
                            contentClassName == null
                                ? ``
                                : ` ${contentClassName}`
                        }`}
                        style={{ maxHeight: height ?? undefined }}
                    >
                        {children}
                    </div>
                    <div className="modal-footer">{modalFooter}</div>
                </div>
            )}
        </>
    );
}

function ModalOKFooter({ onConfirm }: { onConfirm?: () => void }): JSX.Element {
    return (
        <div className="flex-row flex-end">
            <input
                type="button"
                className="confirm-button"
                value="OK"
                onClick={() => {
                    onConfirm?.();
                }}
            />
        </div>
    );
}

function ModalOKCancelFooter({
    onClose,
    onConfirm,
}: {
    onClose?: () => void;
    onConfirm?: () => void;
}): JSX.Element {
    return (
        <div className="flex-row flex-end">
            <input
                type="button"
                className="cancel-button"
                value="Cancel"
                onClick={() => {
                    onClose?.();
                }}
                style={{ marginRight: "8px" }}
            />
            <input
                type="button"
                className="confirm-button"
                value="OK"
                onClick={() => {
                    onConfirm?.();
                }}
            />
        </div>
    );
}

function ModalSubmitCancelFooter({
    onClose,
    onConfirm,
}: {
    onClose?: () => void;
    onConfirm?: () => void;
}): JSX.Element {
    return (
        <div className="flex-row flex-end">
            <input
                type="button"
                className="cancel-button"
                value="Cancel"
                onClick={() => {
                    onClose?.();
                }}
                style={{ marginRight: "8px" }}
            />
            <input
                type="button"
                className="confirm-button"
                value="Submit"
                onClick={() => {
                    onConfirm?.();
                }}
            />
        </div>
    );
}
